import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { selectMaintenanceStatus } from '@hozana/api/selectors'
import { useIsHydrated } from '@hozana/hooks/useIsHydrated'
import { Translate } from '@hozana/intl/Translate'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { useScreen } from '@hozana/screen/useScreen'
import { type TResponsiveVisibilityProps, responsiveVisibility } from '@hozana/styling/responsive'
import { QA } from '@hozana/tests/constants'
import { GTM_AUTH_SOURCE, TRACKING_SOURCE } from '@hozana/tracking/constants'

import type { TButtonColorName } from 'elements/button/BaseButton'
import { Button } from 'elements/button/Button'
import { Cell } from 'elements/layout/Cell'
import { Div, TDivProps } from 'elements/layout/Div'
import { Row } from 'elements/layout/Row'
import { EllipsisMultiLines } from 'elements/text/EllipsisMultiLines'
import { Link, TLinkProps } from 'elements/text/Link'
import { Logo } from 'elements/ui/Logo'

import { ICON } from 'config/icons'
import { openPopin } from 'general/actions'
import { DONATION_SOURCE, FULL_STATIC_PAGES, QUERY_AUTH_SOURCE } from 'general/constants'
import { POPINS } from 'general/managers/popins/constants'
import { PAGE, newRoutes } from 'routes/constants'

import { JoinButton } from 'modules/community/components/JoinButton'
import type { TJoinButtonProps } from 'modules/community/types'

import { LanguageItem } from './LanguageItem'

export const HeaderItemWrapper = styled(Div).attrs(() => ({
  /* The header-item-wrapper class is used by full-ssr-functions #headerSizeToggling */
  className: 'header-item-wrapper',
}))`
  height: ${({ theme }) => theme.sizes.headerItem};

  /* Header size toggling on HomePage #headerSizeToggling */
  transition: height 0.3s;
  &.big-header-item-wrapper {
    height: 5em !important;
  }
`

export type TTitlePageProps = {
  bigTitle?: boolean
}

/** Screen is defined to "xs: true" by default in SSR, even on desktop,
 * so we force that component to be visible only on mobile, by CSS
 */
export const TitlePage: TFCC<TTitlePageProps> = ({ children, bigTitle }) => (
  <HeaderItemWrapper as={Row} visible="xs">
    <Cell flex="1" m="auto 0px" p="0 5px 0 5px" maxH="38px" noOverflow>
      <EllipsisMultiLines
        fontSize={bigTitle ? '1em' : '1em'}
        lineHeight="1em"
        color="black70"
        lines={bigTitle ? '1' : '2'}
      >
        {children}
      </EllipsisMultiLines>
    </Cell>
  </HeaderItemWrapper>
)

export const LanguageItemBlock: TFC = () => (
  <HeaderItemWrapper align="middleCenter" w="auto">
    <LanguageItem />
  </HeaderItemWrapper>
)

export const LogoBlock: TFC = () => {
  const { t } = useTranslation()
  const screen = useScreen()
  const { pathname } = useRouter()

  const isSsrOnlyPage = FULL_STATIC_PAGES.includes(pathname)
  const fullLogo = screen.lg || screen.xl || isSsrOnlyPage

  return (
    <Link m="auto" data-testid={QA.COMMON.HEADER.LOGO_BUTTON} to={{ pathname: PAGE.HOME }}>
      <HeaderItemWrapper
        as={Div}
        align="middleCenter"
        id={fullLogo ? 'header-logo-full' : 'header-logo-small'}
        w="auto"
        m="0 10px"
      >
        {__CLIENT__ || isSsrOnlyPage ? (
          <Logo full={fullLogo} color="black30" fill="black30" label={t('common:app.back-to-home')} />
        ) : null}
      </HeaderItemWrapper>
    </Link>
  )
}

export const DonationLink: TFC = () => {
  const {
    i18n: { language },
  } = useTranslation()

  return (
    <HeaderItemWrapper align="middleCenter">
      <Button
        id="donation-button"
        icon={ICON.HEART}
        color="red"
        bg="lightRed"
        hoverBg="lightRed"
        borderColor="lightRed"
        hoverColor="darkRed"
        hoverBorderColor="lightRed"
        data-testid={QA.COMMON.HEADER.DONATE_BUTTON}
        valueLink={{
          to: newRoutes.donateAuto({ locale: language, source: DONATION_SOURCE.HEADER }),
          reload: true,
        }}
      >
        trans:common:app.i-donate
      </Button>
    </HeaderItemWrapper>
  )
}

export type TLinkButtonProps = TResponsiveVisibilityProps & Pick<TLinkProps, 'to'>

const ButtonWithVisibility = styled(Button)`
  ${responsiveVisibility}
`

export const HeaderLinkButton: TFCC<TLinkButtonProps> = ({ to, children, visible }) => (
  <ButtonWithVisibility
    style={{ alignItems: 'center', justifyContent: 'center' }}
    visible={visible}
    w="auto"
    m="0 5px 0 0"
    valueLink={to && { to }}
    reverseColors
  >
    {children}
  </ButtonWithVisibility>
)

export type TConnectButtonProps = {
  'data-testid'?: string
  isLogin?: boolean
  reverseColors?: boolean
  colors?: TButtonColorName
} & TDivProps

export const ConnectButton: TFCC<TConnectButtonProps> = ({
  children,
  isLogin,
  'data-testid': dataTestId,
  reverseColors,
  colors,
  ...otherProps
}) => {
  const { pathname } = useRouter()
  const isHydrated = useIsHydrated()
  const dispatch = useDispatch()

  const onClick = () => {
    dispatch(
      openPopin(POPINS.ConnectPopin, {
        authSource: isLogin ? GTM_AUTH_SOURCE.HEADER_LOGIN : GTM_AUTH_SOURCE.HEADER_SIGNUP,
        connectDetails: {
          displayWizardOnSignup: true,
          ...(pathname === PAGE.HOME && { reloadUrl: newRoutes.prayerSpace(), reload: true }),
        },
      }),
    )
  }

  return (
    <HeaderItemWrapper align="middleCenter" w="auto" m="0 5px 0 0" {...otherProps}>
      <Button
        onClick={onClick}
        reverseColors={reverseColors}
        colors={colors}
        data-testid={dataTestId}
        valueLink={
          !isHydrated && {
            to: {
              pathname: PAGE.CONNECT,
              query: {
                authSource: isLogin ? QUERY_AUTH_SOURCE.HEADER_LOGIN : QUERY_AUTH_SOURCE.HEADER_SIGNUP,
              },
            },
          }
        }
      >
        {children}
      </Button>
    </HeaderItemWrapper>
  )
}

export const LoginButton: TFC<Omit<TConnectButtonProps, 'isLogin'>> = (props) => (
  <ConnectButton isLogin data-testid={QA.COMMON.HEADER.LOGIN_BUTTON} {...props}>
    trans:common:auth.login.login
  </ConnectButton>
)

export const SignupButton: TFC<Omit<TConnectButtonProps, 'isLogin'>> = (props) => (
  <ConnectButton data-testid={QA.COMMON.HEADER.SIGNUP_BUTTON} invisible="xs" {...props}>
    trans:common:auth.signup.signup
  </ConnectButton>
)

export type TCommunityJoinButtonsProps = Pick<TJoinButtonProps, 'community'>

export const CommunityJoinButtons: TFC<TCommunityJoinButtonsProps> = ({ community }) => {
  const screen = useScreen()
  const isMaintenanceStateActive = useSelector((state) => selectMaintenanceStatus(state))

  return (
    !isMaintenanceStateActive && (
      <HeaderItemWrapper align="middleCenter" w="auto" m="0 5px 0 0">
        <JoinButton
          community={community}
          trackingSource={TRACKING_SOURCE.HEADER}
          // No event origin here because it is already on the community's page
        >
          {!(screen.xs || screen.sm) ? (
            <Translate values={{ lifetime: community.lifetime }}>trans:common:community.join.long</Translate>
          ) : undefined}
        </JoinButton>
      </HeaderItemWrapper>
    )
  )
}
